// Issue a client-portal link for a seller's (hidden) listing file and email
// them a link to their listing forms — usable BEFORE the listing is signed.
// Seller counterpart of sendBuyerPortalForms.js: mirrors ClientPortalPanel's
// send flow, with listing-appropriate copy and the property address. Best-effort:
// returns { ok, reason?, url?, demo? } and never throws for expected conditions.
import { issuePortal, IS_DEV, makeDemoPortal } from "./clientPortal";
import { getContactsByIds } from "./firebasefunctions";
import { sendTransactionEmail } from "./emailSender";
import { plainTextToHtml, wrapBrandedEmail } from "./emailBrandTemplate";
import { buildButton } from "./openHouseEmailsHtml";
import { recordSentEmail } from "./sentEmails";
import { listClients } from "./leadClients";
import { emailsForContacts, splitToBcc, pickEmail, pickName } from "./emailFanout";

export async function sendSellerPortalForms(deal, lead, user = {}) {
  const dealId = String(deal?.createdat || deal?.id || "");
  const clients = listClients(lead);
  const contactIds = clients.map((c) => c.contactId).filter(Boolean);
  if (!dealId || !contactIds.length)
    return { ok: false, reason: "missing file or contact" };

  // ONE magic link, seeded with the primary — the portal gate already unlocks
  // for any co-client's last name, so every client can use this same link.
  const issued = await issuePortal({ dealId, contactId: contactIds[0] });
  const portal = issued.ok || !IS_DEV ? issued : makeDemoPortal(dealId);
  if (!portal.ok) return { ok: false, reason: "could not create portal link" };

  // Resolve EVERY client's email — the forms go to all of them, not just one.
  let contacts = [];
  try {
    contacts = (await getContactsByIds(contactIds)) || [];
  } catch (_) {
    /* fall through to lead fields */
  }
  let emails = emailsForContacts(contacts);
  if (!emails.length && pickEmail(lead)) emails = [pickEmail(lead)];
  const { to, bcc, all } = splitToBcc(emails);
  const toName =
    pickName(contacts[0]) || clients[0]?.contactName || lead?.contactName || "";
  if (!to) return { ok: false, reason: "no email on file", url: portal.url };

  // Sandbox: don't fire a real email at a synthetic token.
  if (IS_DEV) return { ok: true, url: portal.url, demo: true };

  const address = deal?.propertyaddress || lead?.propertyAddress || "";
  const firstName = toName.split(" ")[0] || "there";
  const subject = address
    ? `Your listing forms — ${address}`
    : "Your listing forms — Team Edelstein";
  const intro =
    `Hi ${firstName},\n\n` +
    `As we get ${address || "your home"} ready to list, we've set up a quick set of online forms for you — the details that help us market it and prep the paperwork. They only take a few minutes, and your answers save as you go.`;
  const outro = `Thank you!\nTeam Edelstein`;
  const text = `${intro}\n\nOpen your secure link here:\n${portal.url}\n\n${outro}`;
  const html = wrapBrandedEmail({
    contentHtml:
      plainTextToHtml(intro) +
      `<p style="margin:18px 0;">${buildButton(portal.url, "Open Your Listing Forms")}</p>` +
      plainTextToHtml(outro),
    sender: user,
    propertyAddress: address,
    videoCard: "",
  });

  const res = await sendTransactionEmail({
    userId: user?.id,
    to,
    toName,
    bcc,
    subject,
    text,
    html,
    emailId: "seller-portal-forms",
    relatedDealId: dealId,
  });
  if (!res?.ok)
    return { ok: false, reason: res?.error || "send failed", url: portal.url };

  try {
    await recordSentEmail({
      dealId,
      dealAddress: address,
      emailId: "seller-portal-forms",
      sequenceSide: "Seller",
      agentId: user?.id || null,
      agentName: user?.displayname || "",
      recipientEmail: all.join(", "),
      recipientName: toName,
      subject,
      bodyHtml: html,
      attachments: [],
      videoUrl: "",
      gmailMessageId: res.gmailMessageId || null,
    });
  } catch (e) {
    console.error("sent-email history write failed (email DID send):", e);
  }
  return { ok: true, url: portal.url, recipients: all };
}
